import { nanoid } from '@reduxjs/toolkit';
import round from 'lodash/round';

import { PLAYER_TIME_PRECISION } from 'constants/video';
import { getComplementRanges } from 'utilities/ranges';

export {
  convertTrimTimeToFullTime,
  convertFullTimeToTrimTime,
  getTrimVideoDuration,
  getTrimEventProperties,
} from '../../../shared/utilities/trimming';

const DEFAULT_TRIM_RANGE_LENGTH = 0.2; // percentage (20%) of video

// given a current trim range, the array of all other trim ranges, duration of
// the video, and an optional time padding for the bounds, return a bounding
// range that doesn't allow the current trim range to extend into other ranges
// or exceed the time bounds of the video
//
// all times are in *milliseconds*
export const getBoundingRange = (
  currentTrimRange,
  trimRanges,
  duration,
  padding = 1
) => {
  const boundingRange = { from: 0, to: duration };

  // find the bounds for this trim range
  // {
  //    from: `0` or the closest `to` segment + <padding>
  //    to:   `duration` of video or the closest `from` segment + <padding>
  // }
  trimRanges.some(trimRange => {
    if (trimRange.to <= currentTrimRange.from) {
      boundingRange.from = trimRange.to + padding;
    }

    if (trimRange.from >= currentTrimRange.to) {
      boundingRange.to = trimRange.from - padding;

      return true;
    }
  });

  return boundingRange;
};

// given a current time, new time, duration of the video, and an array of trim
// ranges, give a new time by skipping over any ranges
//
// expects: currentTime, newTime, and duration to be in *milliseconds*
// expects: trimRanges to take the form { from: milliseconds, to: milliseconds }
// returns: time in seconds
export const getNewTimeSkipRanges = (
  currentTime,
  newTime,
  duration,
  trimRanges,
  currentTrimRange,
  checkMedian = false
) => {
  let newTimeToReturn = newTime;

  const currentTrimRangeId =
    currentTrimRange != null ? currentTrimRange.id : null;
  const movingLeft = newTime < currentTime;

  for (const trimRange of trimRanges) {
    if (
      currentTrimRangeId === trimRange.id ||
      trimRange.from >= newTime ||
      trimRange.to <= newTime
    ) {
      continue;
    }

    // initialize the new time to be to the left or right of the closest trim
    // range
    newTimeToReturn = !movingLeft ? trimRange.from - 1 : trimRange.to + 1;

    const overTrimMedian = newTime > (trimRange.from + trimRange.to) / 2;

    // skip over the range
    if (!movingLeft && (!checkMedian || overTrimMedian)) {
      if (trimRange.to >= duration) {
        newTimeToReturn = trimRange.from - 1;
      } else {
        newTimeToReturn = trimRange.to + 1;
      }
    } else if (!movingLeft && (!checkMedian || !overTrimMedian)) {
      if (trimRange.from <= 0) {
        newTimeToReturn = trimRange.to + 1;
      } else {
        newTimeToReturn = trimRange.from - 1;
      }
    }
  }

  return newTimeToReturn;
};

export const waitingForTrim = (video, state) => {
  const { processing_information: processingInformation } = video;

  if (processingInformation == null) {
    return false;
  }

  const { in_trim_mode: inTrimMode } = state.trimming;
  const {
    trim_id: trimId,
    trim_progress: trimProgress,
    instant_editing_enabled: instantEditingEnabled,
  } = processingInformation;

  return (
    !inTrimMode &&
    !instantEditingEnabled &&
    trimId != null &&
    trimProgress !== 100
  );
};

// give a current time, duration of the video and the trim ranges, create a
// suitable trim range at the current time
//
// all times in *milliseconds*
export const createCurrentTrimRange = (currentTime, duration, trimRanges) => {
  let currentTrimRangeStart = currentTime;
  let currentTrimRangeEnd;

  let trimRangeAfterStart = null;
  let trimRangeBeforeStart = null;

  trimRanges.some(trimRange => {
    if (trimRange.to <= currentTrimRangeStart) {
      trimRangeBeforeStart = trimRange;
    }

    if (trimRange.from > currentTrimRangeStart) {
      trimRangeAfterStart = trimRange;

      return true;
    }
  });

  // end the trim segment as either 10% from this current point, the end
  // of the video, or the beginning of the next trim segment
  currentTrimRangeEnd = Math.min(
    currentTrimRangeStart + duration * DEFAULT_TRIM_RANGE_LENGTH,
    duration,
    trimRangeAfterStart ? trimRangeAfterStart.from - 1 : duration
  );

  // check to see if the range is right at the end and empty
  // if so, try to extend it backwards
  if (
    currentTrimRangeStart === currentTrimRangeEnd &&
    currentTrimRangeEnd === duration
  ) {
    currentTrimRangeEnd = currentTrimRangeStart;
    currentTrimRangeStart = Math.max(
      currentTrimRangeStart - duration * DEFAULT_TRIM_RANGE_LENGTH,
      trimRangeBeforeStart ? trimRangeBeforeStart.to + 1 : 0
    );
  }

  return {
    from: currentTrimRangeStart,
    to: currentTrimRangeEnd,
  };
};

// given the requested trim bound time, the current trim range, the bounding range,
// whether we're moving the lower or upper bound, and an optional time padding,
// return a new bounding time that falls within the bounding range and does not
// cause the lower bound to exceed the upper bound
//
// all times are in *milliseconds*
export const getNewTrimBoundTime = (
  requestedTime,
  currentTrimRange,
  boundingRange,
  isLowerBound = true,
  padding = 1
) => {
  let newBoundTime;

  if (isLowerBound) {
    newBoundTime = Math.max(requestedTime, boundingRange.from);
    newBoundTime = Math.min(newBoundTime, currentTrimRange.to - padding);
  } else {
    newBoundTime = Math.min(requestedTime, boundingRange.to);
    newBoundTime = Math.max(newBoundTime, currentTrimRange.from + padding);
  }

  return newBoundTime;
};

export const makePlayRangeId = () => nanoid();

export const isTimeInPlayRange = (currentTime, { from, to }) =>
  from <= currentTime && to >= currentTime;

export const findCurrentPlayRangeIdx = (currentTime, playRanges) => {
  const currentPlayRangeIdx = playRanges.findIndex(range =>
    isTimeInPlayRange(currentTime, range)
  );

  return currentPlayRangeIdx;
};

export const findNewTimeInPlayRanges = (currentTime, delta, playRanges) => {
  playRanges = playRanges.slice();
  playRanges.sort((a, b) => a.from - b.from);

  const currentPlayRangeIdx = findCurrentPlayRangeIdx(currentTime, playRanges);
  const currentPlayRange = playRanges[currentPlayRangeIdx];

  // if currentTime + delta is in same play range, then just return that
  if (isTimeInPlayRange(currentTime + delta, currentPlayRange)) {
    return currentTime + delta;
  }

  const nextPlayRange =
    delta > 0
      ? playRanges[currentPlayRangeIdx + 1]
      : playRanges[currentPlayRangeIdx - 1];

  const hasNextPlayRange = nextPlayRange != null;

  let timeLeftInCurPlayRange;
  let nextCurTime;

  if (delta > 0) {
    if (!hasNextPlayRange) {
      return currentPlayRange.to;
    }

    timeLeftInCurPlayRange = currentPlayRange.to - currentTime;
    nextCurTime = nextPlayRange.from;
  } else {
    if (!hasNextPlayRange) {
      return currentPlayRange.from;
    }

    timeLeftInCurPlayRange = currentPlayRange.from - currentTime;
    nextCurTime = nextPlayRange.to;
  }

  return findNewTimeInPlayRanges(
    nextCurTime,
    delta - timeLeftInCurPlayRange,
    playRanges.filter((range, idx) => idx != currentPlayRangeIdx)
  );
};

export const makePlayRangesFromTrimRanges = (trimRanges, duration) => {
  let newRanges = getComplementRanges(trimRanges, 0, duration * 1000).map(
    ({ from, to }) => ({
      id: makePlayRangeId(from, to),
      from: from / 1000,
      to: to / 1000,
    })
  );

  if (newRanges.length > 1) {
    const lastIdx = newRanges.length - 1;
    const lastRange = newRanges[lastIdx];

    // This is a guard against a "ghost range" being added to the very end.
    // This is a temporary fix until we make play ranges the source of truth.
    // TODO Remove this as soon as possible.
    if (lastRange.to === duration && lastRange.to - lastRange.from < 0.1) {
      newRanges = newRanges.slice(0, lastIdx);
    }
  }

  return newRanges;
};

export const makeTrimRangesFromPlayRanges = (playRanges, duration) => {
  return getComplementRanges(playRanges, 0, duration).map(
    ({ from, to }, idx) => ({
      id: Number(new Date()) + idx,
      from: round(from * 1000, PLAYER_TIME_PRECISION - 3),
      to: round(to * 1000, PLAYER_TIME_PRECISION - 3),
    })
  );
};
